import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormControl, Select as SelectMui } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useTheme } from 'contexts/ThemeContext';

import pluralize from 'utils/pluralize';
import { referencial } from 'utils/ref';

import { HelpTooltip, Text, Tooltip } from 'components/DataDisplay';
import { Button, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Accordion } from 'components/Surfaces';

import { Loading } from '../Loading';

import { Checkbox } from './Checkbox';

const CustomOption = ({
  datas: { items = [], key, label },
  onChange,
  addAll,
  removeAll,
  selectedItems = []
}) => {
  const { panelColor, themeColors } = useTheme();
  const colorRules = themeColors[panelColor];

  const deepCountSelected = datas => {
    if (!datas?.items?.length) {
      const isSelected = selectedItems.findIndex(el => el.key === datas.key);
      return isSelected !== -1 ? 1 : 0;
    }

    return datas.items.reduce((acc, item) => {
      return acc + deepCountSelected(item);
    }, 0);
  };

  const deepItems = data => {
    // eslint-disable-next-line no-shadow
    const { key, label, items } = data;
    if (!items?.length) {
      return [{ key, label }];
    }

    return items.reduce((acc, item) => {
      return [...acc, ...deepItems(item)];
    }, []);
  };

  const onAllChange = list => {
    const notSelected = list.filter(
      el =>
        !selectedItems.find(
          // eslint-disable-next-line no-shadow
          ({ key, label }) => key === el.key && label === el.label
        )
    );
    if (notSelected.length) {
      addAll(notSelected);
    } else {
      removeAll(list);
    }
  };

  const isSelected = selectedItems.findIndex(el => el.key === key) !== -1;
  const nbSelected = deepCountSelected({ items, key, label });

  const itemsList = items.reduce((acc, item) => {
    return [...acc, ...deepItems(item)];
  }, []);

  return items.length !== 0 ? (
    <Row justify="flex-start" spacing={0}>
      <Item>
        <Accordion
          rows={[
            {
              key,
              label: (
                <Row alignItems="center" spacing={0} justify="flex-start">
                  <Item
                    flex
                    style={{
                      marginRight: '5px'
                    }}
                  >
                    <Checkbox
                      color={colorRules}
                      checked={nbSelected === itemsList.length}
                      onChange={() => onAllChange(itemsList)}
                    />
                  </Item>
                  <Item xs justify="flex-start">
                    <Tooltip title={label}>
                      <Text fontSize="13px" nowrap>
                        {label}
                      </Text>
                    </Tooltip>
                  </Item>
                  {nbSelected !== 0 && (
                    <Item xs={12} justify="flex-end">
                      <Text color={colorRules}>{` (${nbSelected})`}</Text>
                    </Item>
                  )}
                </Row>
              ),
              items: items.map((el, index) => {
                const customKey = `${el.key}_${index}`;
                return (
                  <CustomOption
                    key={customKey}
                    datas={el}
                    addAll={addAll}
                    removeAll={removeAll}
                    onChange={onChange}
                    selectedItems={selectedItems}
                  />
                );
              })
            }
          ]}
        />
      </Item>
    </Row>
  ) : (
    <Row
      alignItems="center"
      justify="flex-start"
      spacing={0}
      style={{ padding: '0px 9px' }}
    >
      <Item flex>
        <Checkbox
          color={colorRules}
          checked={isSelected}
          onChange={() => onChange({ key, label })}
        />
      </Item>
      <Item xs justify="flex-start">
        <Tooltip title={label || key}>
          <Text fontSize="13px" nowrap>
            {label || key}
          </Text>
        </Tooltip>
      </Item>
    </Row>
  );
};

export const CustomSelect = props => {
  const {
    title,
    required,
    disabled,
    color,
    onChange,
    options,
    selected = [],
    small,
    bgColor,
    selectAll,
    searchBar,
    hideNone,
    filterOthers,
    placeholder,
    width,
    limit = 20,
    type = '',
    textColor,
    multiple,
    tooltip
  } = props;

  const { themeColors } = useTheme();
  const {
    line,
    teal,
    tealGlow,
    ink,
    inkSoft,
    faint
  } = themeColors.designSystem;

  const [localOptions, setLocalOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [optionsDisp, setOptionsDisp] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [searched, setSearch] = useState('');
  const colorRules = themeColors[color] || color || line;
  const selectAllDataKey = 'selectAll';

  const selectAllData = {
    key: selectAllDataKey,
    label: 'Tout sélectionner'
  };
  const newOptions = [];
  if (!hideNone) {
    newOptions.push({
      label: filterOthers,
      key: null
    });
  }

  const allOptions = newOptions.concat(localOptions);

  const verifSelectAll = el => el.key === selectAllDataKey;
  const isSelectedOption = (selectedValues = [], option) => {
    return selectedValues.some(el => {
      return typeof el === 'object' ? el.key === option.key : el === option.key;
    });
  };

  const getInitialSelectedItems = opts => {
    if (multiple) {
      return opts.filter(option => isSelectedOption(selected, option));
    }
    if (selected && typeof selected === 'string') {
      const option = opts.find(opt => opt.key === selected);
      return option ? [option] : [];
    }
    return [];
  };

  const getAllItems = items => {
    return items.reduce((acc, el) => {
      if (el?.items?.length > 0) {
        acc.push(...getAllItems(el.items));
      } else {
        acc.push(el);
      }
      return acc;
    }, []);
  };

  const allIsSelected = selItems => {
    if (!selectAll) {
      return false;
    }
    const opt = getAllItems(optionsDisp);
    const tmp = selItems.filter(el => opt.find(e => el.key === e.key));
    return tmp.length === opt.length;
  };

  const useStyles = makeStyles({
    root: {
      // '& label.Mui-focused': {
      //   color: themeColors.activation
      // },
      '& .MuiInputBase-root': {
        color: textColor || ink,
        backgroundColor: bgColor || 'transparent',
        fontSize: '12px',
        borderRadius: '6px',
        minHeight: '30px'
      },
      '& .MuiSelect-select': {
        padding: '5px 32px 5px 9px'
      },
      '& .MuiSelect-icon': {
        color: textColor || faint
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: colorRules
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: colorRules
        },
        '&:hover fieldset': {
          borderColor: colorRules
        },
        '&.Mui-focused fieldset': {
          borderColor: teal
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 2px ${tealGlow}`
        }
      }
    }
  });

  const classes = useStyles();

  // eslint-disable-next-line consistent-return
  const onCheck = ({ key, label }) => {
    if (!multiple) {
      if (selectedItems[0]?.key === key) {
        setSelectedItems([]);
      } else {
        setSelectedItems([{ key, label }]);
      }
      return;
    }

    const tmp = [...selectedItems];
    const existIndex = selectedItems.findIndex(el => el.key === key);
    if (existIndex !== -1) {
      setSelectedItems(
        tmp.filter(el => !(el.key === key) && !verifSelectAll(el))
      );
      return;
    }
    tmp.push({ key, label });
    if (allIsSelected(tmp)) {
      tmp.push(selectAllData);
    }
    setSelectedItems(tmp);
  };

  const addAll = list => {
    const tmp = [...selectedItems, ...list];
    if (allIsSelected(tmp)) {
      tmp.push(selectAllData);
    }
    setSelectedItems(tmp);
  };

  const removeAll = list => {
    const tmp = selectedItems.filter(
      el => !list.find(({ key }) => key === el.key || verifSelectAll(el))
    );
    setSelectedItems(tmp);
  };

  const keepItemSelected = opt => {
    const rslt = selectedItems.reduce((acc, el) => {
      if (!opt.find(e => e.key === el.key) && !verifSelectAll(el)) {
        acc.push(el);
      }
      return acc;
    }, []);
    return rslt;
  };

  const getAllItemsBySearch = (items, sW) => {
    const purify = str => {
      return typeof str === 'string'
        ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        : '';
    };

    const regex = '[A-Za-zÀ-ÖØ-öø-ÿ0-9]+';
    const tbExp = sW.match(new RegExp(regex, 'gi')) || [];

    const result = items
      .filter(o => !o[selectAllDataKey])
      .filter(item => {
        for (let i = 0; i < tbExp.length; i += 1) {
          const searchedWord = purify(tbExp[i].toUpperCase());
          if (!new RegExp(searchedWord, 'gi').test(item.label)) {
            return false;
          }
        }
        return true;
      });
    return result;
  };

  const onSearch = value => {
    setSearch(value);
    const result = getAllItemsBySearch(allOptions, value);
    setOptionsDisp(result);
  };

  const onSelectAll = ({ key, label }) => {
    const existIndex = selectedItems.findIndex(el => el.key === key);
    if (existIndex !== -1) {
      return setSelectedItems(keepItemSelected(getAllItems(optionsDisp)));
    }
    return setSelectedItems([
      { key, label },
      ...selectedItems,
      ...getAllItems(optionsDisp)
    ]);
  };

  const handleData = () => {
    if (localOptions.length) {
      const sWLenth = searched.length;

      if (!sWLenth) {
        setOptionsDisp(allOptions);
      }
    }
  };

  const initOptions = async () => {
    let newOption = options;

    if (typeof newOption === 'string') {
      newOption =
        typeof referencial[options] === 'function'
          ? await referencial[options]()
          : [];
    }
    if (newOption.length === 0 && localOptions.length > 0) {
      setLoaded(true);
      return;
    }

    setLocalOptions(newOption);
    const newSelected = getInitialSelectedItems(newOption);

    setSelectedItems(newSelected);
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded || localOptions.length === 0) {
      return;
    }

    let newSelected = [];

    if (multiple) {
      newSelected = localOptions.filter(option =>
        isSelectedOption(selected, option)
      );
    } else if (selected) {
      if (typeof selected === 'object' && !Array.isArray(selected)) {
        const option = localOptions.find(opt => opt.key === selected.key);
        newSelected = option ? [option] : [];
      } else {
        const option = localOptions.find(opt => opt.key === selected);
        newSelected = option ? [option] : [];
      }
    }

    setSelectedItems(newSelected);

    // eslint-disable-next-line
  }, [selected, multiple, loaded, localOptions]);

  useEffect(() => {
    handleData();

    // eslint-disable-next-line
  }, [localOptions, open, searched]);

  useEffect(() => {
    initOptions();

    // eslint-disable-next-line
  }, [options]);

  return (
    <FormControl
      size="small"
      variant="outlined"
      className={classes.root}
      style={{
        padding: small && '6px 0px',
        width: '100%'
      }}
    >
      {title && (
        <Row spacing={2} justify="flex-start" alignItems="center">
          <Item justify="flex-start" flex>
            <Text noWrap color={inkSoft} fontWeight={600} fontSize="11px">
              {title === '&nbsp;' ? <>&nbsp;</> : title}
              {required && '*'}
            </Text>
          </Item>
          {tooltip && (
            <Item
              flex
              style={{
                alignItem: 'center'
              }}
            >
              <HelpTooltip title={tooltip} />
            </Item>
          )}
        </Row>
      )}
      <SelectMui
        disabled={disabled || !loaded}
        displayEmpty
        style={{
          height: small && '30px',
          marginTop: title ? '5px' : undefined
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          }
        }}
        multiple={multiple}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        onOpen={() => {
          setOpen(true);
        }}
        renderValue={() => {
          let displayText = placeholder || '';

          if (multiple) {
            if (
              selectedItems.length === allOptions.length ||
              selectedItems.find(o => o.key === selectAllDataKey)
            ) {
              displayText = 'Tous';
            } else if (selectedItems.length > 0) {
              displayText = selectedItems
                .reduce(
                  (acc, e) =>
                    e.key !== selectAllDataKey ? acc.concat(e.label) : acc,
                  []
                )
                .join(', ');
            }
          } else if (selectedItems.length > 0) {
            displayText = selectedItems[0]?.label || '';
          }

          return (
            <Loading loading={!loaded}>
              <Text
                nowrap
                style={{
                  maxWidth: '200px',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  textAlign: 'justify'
                }}
              >
                {displayText}
              </Text>
            </Loading>
          );
        }}
        value={selectedItems}
      >
        <Container style={{ width }} spacing={0}>
          {searchBar && (
            <TextField
              width="95%"
              placeholder="Rechercher..."
              fontSize="smaller"
              clearable
              small
              value={searched}
              onChange={onSearch}
              color={themeColors.themeV2.inputColor}
            />
          )}
          <Row
            spacing={0}
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            {selectAll && !searched.length && (
              <CustomOption
                datas={selectAllData}
                onChange={onSelectAll}
                selectedItems={selectedItems}
              />
            )}
            {optionsDisp.slice(0, limit || optionsDisp.length).map(option => {
              return (
                <CustomOption
                  key={option.id || option.key}
                  datas={option}
                  onChange={onCheck}
                  addAll={addAll}
                  removeAll={removeAll}
                  selectedItems={selectedItems}
                />
              );
            })}
            {limit && limit < optionsDisp.length && (
              <Text style={{ display: 'flex' }}>
                +<Text color={textColor}>{optionsDisp.length - limit}</Text>
                &nbsp;{pluralize(type, optionsDisp.length - limit)}
                &nbsp;restantes
              </Text>
            )}
          </Row>
          <Row>
            <Item>
              <Button
                variant="contained"
                small
                onClick={() => {
                  setOpen(false);
                  if (multiple) {
                    onChange(selectedItems);
                  } else {
                    onChange(selectedItems[0]?.key || '');
                  }
                }}
              >
                Valider
              </Button>
            </Item>
          </Row>
        </Container>
      </SelectMui>
    </FormControl>
  );
};

CustomSelect.defaultProps = {
  color: null,
  onChange: () => null,
  options: [],
  small: false,
  bgColor: null,
  selected: null,
  width: '280px',
  title: null,
  required: null,
  disabled: false,
  selectAll: false,
  searchBar: false,
  filterOthers: 'Aucune',
  placeholder: '',
  multiple: false
};

CustomSelect.propTypes = {
  color: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  small: PropTypes.bool,
  selected: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.shape({
      key: PropTypes.any,
      label: PropTypes.string
    })
  ]),
  bgColor: PropTypes.string,
  width: PropTypes.string,
  title: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  selectAll: PropTypes.bool,
  searchBar: PropTypes.bool,
  filterOthers: PropTypes.string,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool
};

export default CustomSelect;
